import { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import {
  getAdminInviteCodes,
  createAdminInviteCodes,
  getAdminPlans,
  createAdminPlan,
  deleteAdminPlan,
  revokeAdminInviteCode,
} from '@/services/subscriptionApi';
import type { InviteCode, SubscriptionPlan } from '@/types/subscription.types';
import {
  Copy, Check, Plus, Ticket, Tag,
  Hash, DollarSign, Clock, ToggleLeft, ToggleRight,
  Trash2, UserX, Ban,
} from 'lucide-react';

const PLAN_TYPE_LABELS: Record<string, string> = {
  monthly: '月付',
  yearly: '年付',
  lifetime: '永久',
  trial: '试用',
};

const PLAN_TYPE_COLORS: Record<string, string> = {
  monthly: 'bg-blue-50 text-blue-700 border-blue-200',
  yearly: 'bg-purple-50 text-purple-700 border-purple-200',
  lifetime: 'bg-amber-50 text-amber-700 border-amber-200',
  trial: 'bg-gray-50 text-gray-700 border-gray-200',
};

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ── InviteCode Section ──────────────────────────────────────────────────────

function InviteCodeSection({ plans }: { plans: SubscriptionPlan[] }) {
  const { toast } = useToast();
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterUsed, setFilterUsed] = useState<string>('');
  const [filterPlanId, setFilterPlanId] = useState<string>('');
  const [filterKeyword, setFilterKeyword] = useState('');

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createPlanId, setCreatePlanId] = useState('');
  const [createCount, setCreateCount] = useState(10);
  const [createNote, setCreateNote] = useState('');
  const [createExpiresAt, setCreateExpiresAt] = useState('');
  const [creating, setCreating] = useState(false);

  const [generatedCodes, setGeneratedCodes] = useState<string[] | null>(null);
  const [generatedPlanName, setGeneratedPlanName] = useState('');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const params: { isUsed?: boolean; planId?: number; keyword?: string } = {};
      if (filterUsed === 'true') params.isUsed = true;
      if (filterUsed === 'false') params.isUsed = false;
      if (filterPlanId) params.planId = Number(filterPlanId);
      if (filterKeyword.trim()) params.keyword = filterKeyword.trim();
      const data = await getAdminInviteCodes(params);
      setInviteCodes(data);
    } catch {
      toast({ title: '加载失败', description: '无法获取邀请码列表', variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterUsed, filterPlanId, filterKeyword, toast]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async () => {
    if (!createPlanId) {
      toast({ title: '请选择订阅计划', variant: 'warning' });
      return;
    }
    if (createCount < 1 || createCount > 100) {
      toast({ title: '数量需在 1-100 之间', variant: 'warning' });
      return;
    }
    setCreating(true);
    try {
      const res = await createAdminInviteCodes({
        planId: Number(createPlanId),
        count: createCount,
        note: createNote || undefined,
        expiresAt: createExpiresAt || undefined,
      });
      if (res.success) {
        setGeneratedCodes(res.data.codes);
        setGeneratedPlanName(res.data.planName);
        setShowCreateForm(false);
        setCreatePlanId('');
        setCreateCount(10);
        setCreateNote('');
        setCreateExpiresAt('');
        fetchCodes();
        toast({ title: '生成成功', description: `已生成 ${res.data.count} 个邀请码`, variant: 'success' });
      } else {
        toast({ title: '生成失败', description: res.message || '请稍后重试', variant: 'error' });
      }
    } catch {
      toast({ title: '生成失败', description: '请稍后重试', variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const copyCode = async (code: string, index: number) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1500);
    } catch {
      toast({ title: '复制失败', variant: 'error' });
    }
  };

  const copyAllCodes = async () => {
    if (!generatedCodes) return;
    try {
      await navigator.clipboard.writeText(generatedCodes.join('\n'));
      toast({ title: '已复制全部邀请码', variant: 'success' });
    } catch {
      toast({ title: '复制失败', variant: 'error' });
    }
  };

  const handleRevoke = async (id: number) => {
    if (!window.confirm('确定要取消该用户的订阅吗？邀请码将保持作废状态，不可再次使用。')) return;
    try {
      const res = await revokeAdminInviteCode(id);
      if (res.success) {
        fetchCodes();
        toast({ title: '已取消订阅', description: res.message, variant: 'success' });
      } else {
        toast({ title: '操作失败', description: res.message || '请稍后重试', variant: 'error' });
      }
    } catch {
      toast({ title: '操作失败', description: '请稍后重试', variant: 'error' });
    }
  };

  return (
    <div className="space-y-4">
      {/* 筛选器 */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">使用状态</Label>
          <select
            className="border rounded-lg px-3 py-2 text-sm min-w-[120px] bg-white"
            value={filterUsed}
            onChange={(e) => setFilterUsed(e.target.value)}
          >
            <option value="">全部</option>
            <option value="false">未使用</option>
            <option value="true">已使用</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">订阅计划</Label>
          <select
            className="border rounded-lg px-3 py-2 text-sm min-w-[160px] bg-white"
            value={filterPlanId}
            onChange={(e) => setFilterPlanId(e.target.value)}
          >
            <option value="">全部计划</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs text-gray-500 mb-1 block">关键词搜索</Label>
          <Input
            placeholder="搜索邀请码/使用者"
            value={filterKeyword}
            onChange={(e) => setFilterKeyword(e.target.value)}
            className="h-10"
          />
        </div>
        <Button onClick={() => { setShowCreateForm(true); setGeneratedCodes(null); }}>
          <Plus className="w-4 h-4 mr-1" />
          批量生成
        </Button>
      </div>

      {/* 生成表单 */}
      {showCreateForm && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="planId">订阅计划 <span className="text-red-500">*</span></Label>
                <select
                  id="planId"
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  value={createPlanId}
                  onChange={(e) => setCreatePlanId(e.target.value)}
                >
                  <option value="">请选择</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="count">数量 <span className="text-red-500">*</span></Label>
                <Input
                  id="count"
                  type="number"
                  min={1}
                  max={100}
                  value={createCount}
                  onChange={(e) => setCreateCount(Number(e.target.value))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="note">备注</Label>
                <Input
                  id="note"
                  placeholder="可选"
                  value={createNote}
                  onChange={(e) => setCreateNote(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="expiresAt">过期时间</Label>
                <Input
                  id="expiresAt"
                  type="datetime-local"
                  value={createExpiresAt}
                  onChange={(e) => setCreateExpiresAt(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowCreateForm(false)}>取消</Button>
              <Button size="sm" onClick={handleCreate} disabled={creating}>
                {creating ? '生成中...' : '确认生成'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 生成结果 */}
      {generatedCodes && (
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-green-800">
              <Check className="w-4 h-4" />
              已生成 {generatedCodes.length} 个「{generatedPlanName}」邀请码
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {generatedCodes.map((code, i) => (
                <button
                  key={i}
                  onClick={() => copyCode(code, i)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-green-200 rounded-md text-sm font-mono text-green-800 hover:bg-green-100 transition-colors"
                  title="点击复制"
                >
                  {code}
                  {copiedIndex === i ? (
                    <Check className="w-3.5 h-3.5 text-green-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5 text-green-400" />
                  )}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={copyAllCodes}>
              <Copy className="w-4 h-4 mr-1" />
              一键复制全部
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 邀请码表格 */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">加载中...</div>
          ) : inviteCodes.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
              <Ticket className="w-8 h-8 mr-2 opacity-30" />
              暂无邀请码
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">邀请码</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">计划</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">状态</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">使用者</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">使用者信息</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">使用时间</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">过期时间</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">创建时间</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {inviteCodes.map((ic) => (
                    <tr key={ic.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 font-mono text-gray-800 whitespace-nowrap">{ic.code}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-gray-700">{ic.planName}</span>
                        <span className="ml-1.5 text-xs text-gray-400">{PLAN_TYPE_LABELS[ic.planType] || ic.planType}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {!ic.isUsed ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                            未使用
                          </span>
                        ) : ic.hasActiveSubscription ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            已使用
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                            <Ban className="w-3 h-3" />
                            已作废
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {ic.isUsed ? (
                          <span className={!ic.hasActiveSubscription ? 'line-through text-gray-400' : ''}>
                            {ic.usedBy || '-'}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {ic.isUsed ? (
                          <div className={`text-xs space-y-0.5 ${!ic.hasActiveSubscription ? 'opacity-50' : ''}`}>
                            {ic.usedByDisplayName && <div className="font-medium text-gray-800">{ic.usedByDisplayName}</div>}
                            {ic.usedByPhone && <div className="text-gray-500">{ic.usedByPhone}</div>}
                            {ic.usedByPilotNo && <div className="text-gray-500">{ic.usedByPilotNo}</div>}
                            {!ic.usedByDisplayName && !ic.usedByPhone && !ic.usedByPilotNo && '-'}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{formatShortDate(ic.usedAt)}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{formatShortDate(ic.expiresAt)}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{formatShortDate(ic.createdAt)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {ic.isUsed && ic.hasActiveSubscription && (
                          <button
                            onClick={() => handleRevoke(ic.id)}
                            className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 transition-colors"
                            title="取消订阅"
                          >
                            <UserX className="w-4 h-4" />
                            取消订阅
                          </button>
                        )}
                        {ic.isUsed && !ic.hasActiveSubscription && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                            <Ban className="w-3.5 h-3.5" />
                            已撤销
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Plan Section ────────────────────────────────────────────────────────────

function PlanSection({ plans, onPlansChange }: { plans: SubscriptionPlan[]; onPlansChange: () => void }) {
  const { toast } = useToast();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);

  const [name, setName] = useState('');
  const [planType, setPlanType] = useState<SubscriptionPlan['planType']>('monthly');
  const [durationDays, setDurationDays] = useState(30);
  const [price, setPrice] = useState(0);
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  // lifetime 类型自动设为永久有效
  useEffect(() => {
    if (planType === 'lifetime') {
      setDurationDays(0);
    }
  }, [planType]);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast({ title: '请输入计划名称', variant: 'warning' });
      return;
    }
    setCreating(true);
    try {
      await createAdminPlan({
        name: name.trim(),
        planType,
        durationDays,
        price,
        description: description.trim(),
        isActive,
      });
      // DRF ListCreateAPIView POST 返回 201 + 创建的对象（不是 {success:true} 包装）
      // 如果请求失败 axios 会 throw，所以能到这里说明创建成功
      setShowCreateForm(false);
      setName('');
      setPlanType('monthly');
      setDurationDays(30);
      setPrice(0);
      setDescription('');
      setIsActive(true);
      onPlansChange();
      toast({ title: '创建成功', variant: 'success' });
    } catch {
      toast({ title: '创建失败', description: '请稍后重试', variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除该订阅计划吗？')) return;
    try {
      await deleteAdminPlan(id);
      onPlansChange();
      toast({ title: '删除成功', variant: 'success' });
    } catch (error: any) {
      const msg = error.response?.data?.message || '删除失败，请稍后重试';
      toast({ title: '删除失败', description: msg, variant: 'error' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus className="w-4 h-4 mr-1" />
          新建计划
        </Button>
      </div>

      {showCreateForm && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="planName">计划名称 <span className="text-red-500">*</span></Label>
                <Input
                  id="planName"
                  placeholder="例如：年度会员"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="planType">类型</Label>
                <select
                  id="planType"
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  value={planType}
                  onChange={(e) => setPlanType(e.target.value as SubscriptionPlan['planType'])}
                >
                  <option value="monthly">月付</option>
                  <option value="yearly">年付</option>
                  <option value="lifetime">永久</option>
                  <option value="trial">试用</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="durationDays">有效期（天）</Label>
                <Input
                  id="durationDays"
                  type="number"
                  min={0}
                  value={durationDays}
                  onChange={(e) => setDurationDays(Number(e.target.value))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="price">价格（元）</Label>
                <Input
                  id="price"
                  type="number"
                  min={0}
                  step={0.01}
                  value={price}
                  onChange={(e) => setPrice(Number(e.target.value))}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="planDesc">描述</Label>
                <Input
                  id="planDesc"
                  placeholder="可选"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <button
                  type="button"
                  onClick={() => setIsActive(!isActive)}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  {isActive ? (
                    <ToggleRight className="w-6 h-6 text-green-600" />
                  ) : (
                    <ToggleLeft className="w-6 h-6 text-gray-400" />
                  )}
                  {isActive ? '已启用' : '已禁用'}
                </button>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowCreateForm(false)}>取消</Button>
              <Button size="sm" onClick={handleCreate} disabled={creating}>
                {creating ? '创建中...' : '确认创建'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {plans.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
              <Tag className="w-8 h-8 mr-2 opacity-30" />
              暂无订阅计划
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">名称</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">类型</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">有效期</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">价格</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">状态</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {plans.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-medium text-gray-800">{p.name}</span>
                        {p.description && (
                          <span className="ml-2 text-xs text-gray-400">{p.description}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${PLAN_TYPE_COLORS[p.planType] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                          {PLAN_TYPE_LABELS[p.planType] || p.planType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          {p.planType === 'lifetime' ? '永久' : `${p.durationDays} 天`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <DollarSign className="w-3.5 h-3.5 text-gray-400" />
                          {Number(p.price).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {p.isActive ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700">
                            <ToggleRight className="w-4 h-4" />
                            启用
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <ToggleLeft className="w-4 h-4" />
                            禁用
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 transition-colors"
                          title="删除"
                        >
                          <Trash2 className="w-4 h-4" />
                          删除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function SubscriptionAdminPage() {
  const { toast } = useToast();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('invites');

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const data = await getAdminPlans();
      setPlans(data);
    } catch {
      toast({ title: '加载失败', description: '无法获取订阅计划列表', variant: 'error' });
    } finally {
      setPlansLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return (
    <div className="container mx-auto py-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
          <Ticket className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">订阅管理</h1>
          <p className="text-sm text-gray-500">管理邀请码和订阅计划</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="invites" className="inline-flex items-center gap-1.5">
            <Hash className="w-4 h-4" />
            邀请码管理
          </TabsTrigger>
          <TabsTrigger value="plans" className="inline-flex items-center gap-1.5">
            <Tag className="w-4 h-4" />
            订阅计划管理
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invites">
          {plansLoading ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">加载中...</div>
          ) : (
            <InviteCodeSection plans={plans} />
          )}
        </TabsContent>

        <TabsContent value="plans">
          {plansLoading ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">加载中...</div>
          ) : (
            <PlanSection plans={plans} onPlansChange={fetchPlans} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
